/**
 * Settings Page Component
 * User settings: change password, update profile, upload image
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Avatar,
  Alert,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Switch,
  FormControlLabel,
  InputAdornment,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Save as SaveIcon,
  PhotoCamera as PhotoCameraIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Edit as EditIcon,
  VpnKey as VpnKeyIcon,
  Timer as TimerIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useBOM } from '../contexts/BOMContext';
import { useSearchParams } from 'react-router-dom';
import * as authApi from '../services/authApi';
import * as quoteApi from './phase2/quoteApi';
import { LabourParam, MountingMethod } from './phase2/quoteApi';

type SettingsTab = 'user' | 'system' | 'org';

const formatUsd = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
};

const SettingsPage: React.FC = () => {
  const { user, token, refreshUser, switchOrganization } = useAuth();
  const { showToast } = useBOM();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: SettingsTab = (['user', 'system', 'org'].includes(searchParams.get('tab') || '')
    ? searchParams.get('tab')
    : 'user') as SettingsTab;
  const setTab = (next: SettingsTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'user') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };
  const [isLoading, setIsLoading] = useState(false);

  // Profile state
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [profileImage, setProfileImage] = useState<string | null>(null);

  // Password state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Organization state
  const [organizations, setOrganizations] = useState<authApi.Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [switchingOrg, setSwitchingOrg] = useState(false);
  
  // Organization management dialogs
  const [createOrgDialog, setCreateOrgDialog] = useState(false);
  const [editOrgDialog, setEditOrgDialog] = useState(false);
  const [editingOrg, setEditingOrg] = useState<authApi.Organization | null>(null);
  const [newOrgData, setNewOrgData] = useState({
    name: '',
    display_name: '',
    description: '',
    theme: 'ppcanda' as 'ppcanda' | 'indigo',
    logo_url: ''
  });

  const isAdmin = Boolean(user?.is_admin || user?.is_superadmin);
  const [openRouterKey, setOpenRouterKey] = useState('');
  const [openRouterPreview, setOpenRouterPreview] = useState<string | null>(null);
  const [openRouterConfigured, setOpenRouterConfigured] = useState(false);
  const [openRouterCredits, setOpenRouterCredits] = useState<authApi.OpenRouterCredits | null>(null);
  const [llmEnabled, setLlmEnabled] = useState(false);
  const [loadingOpenRouter, setLoadingOpenRouter] = useState(false);
  const [erpKey, setErpKey] = useState('');
  const [erpPreview, setErpPreview] = useState<string | null>(null);
  const [erpConfigured, setErpConfigured] = useState(false);
  const [loadingErp, setLoadingErp] = useState(false);
  const [erpMode, setErpMode] = useState<'demo' | 'live'>('demo');
  const [erpEndpoints, setErpEndpoints] = useState<authApi.ErpEndpointMapping[]>([]);
  const [erpSavedSignature, setErpSavedSignature] = useState('');
  const [confirmLiveErp, setConfirmLiveErp] = useState(false);
  const [labourParams, setLabourParams] = useState<LabourParam[]>([]);
  const [loadingLabourParams, setLoadingLabourParams] = useState(false);
  const [savingLabourParam, setSavingLabourParam] = useState(false);
  const [flagCategories, setFlagCategories] = useState<quoteApi.FlagCategory[]>([]);
  const [newFlagLabel, setNewFlagLabel] = useState('');
  const [mountingMethods, setMountingMethods] = useState<MountingMethod[]>([]);
  const [newMountingName, setNewMountingName] = useState('');
  const [newMountingSeconds, setNewMountingSeconds] = useState('200');
  const [newMountingNoConnect, setNewMountingNoConnect] = useState(false);
  const [savingMounting, setSavingMounting] = useState(false);

  // Load organizations
  useEffect(() => {
    const loadOrganizations = async () => {
      if (!token) return;
      
      setLoadingOrgs(true);
      try {
        if (user?.is_superadmin) {
          // PPCandaAdmin can see all organizations
          const result = await authApi.getAllOrganizations(token);
          setOrganizations(result.organizations);
        } else {
          // Regular users see only their organizations
          const result = await authApi.getOrganizations(token);
          setOrganizations(result.organizations);
        }
        // Set current organization
        if (user?.current_organization_id) {
          setSelectedOrgId(user.current_organization_id);
        } else if (organizations.length > 0) {
          setSelectedOrgId(organizations[0].id);
        }
      } catch (error: any) {
        console.error('Failed to load organizations:', error);
      } finally {
        setLoadingOrgs(false);
      }
    };

    if (user) {
      loadOrganizations();
    }
  }, [user, token]);

  useEffect(() => {
    const loadOpenRouterSettings = async () => {
      if (!token || !isAdmin) return;
      setLoadingOpenRouter(true);
      try {
        const result = await authApi.getOpenRouterSettings(token);
        setOpenRouterConfigured(result.configured);
        setOpenRouterPreview(result.key_preview);
        setOpenRouterCredits(result.credits || null);
        setLlmEnabled(result.llm_enabled);
        setOpenRouterKey('');
      } catch (error) {
        console.error('Failed to load OpenRouter settings:', error);
      } finally {
        setLoadingOpenRouter(false);
      }
    };

    loadOpenRouterSettings();

    const loadErpSettings = async () => {
      if (!token || !isAdmin) return;
      setLoadingErp(true);
      try {
        const result = await authApi.getErpSettings(token);
        setErpConfigured(result.configured);
        setErpPreview(result.key_preview);
        setErpMode(result.mode === 'live' ? 'live' : 'demo');
        setErpEndpoints(result.endpoints || []);
        setErpSavedSignature(JSON.stringify({
          mode: result.mode === 'live' ? 'live' : 'demo',
          endpoints: (result.endpoints || []).map((row) => ({
            key: row.key,
            demo_url: row.demo_url,
            live_url: row.live_url,
          })),
        }));
        setErpKey('');
      } catch (error) {
        console.error('Failed to load ERP settings:', error);
      } finally {
        setLoadingErp(false);
      }
    };
    loadErpSettings();

    const loadLabourParams = async () => {
      if (!isAdmin) return;
      setLoadingLabourParams(true);
      try {
        const [params, flags, methods] = await Promise.all([
          quoteApi.listLabourParams(),
          quoteApi.listFlagCategories(),
          quoteApi.listMountingMethods(),
        ]);
        setLabourParams(params);
        setMountingMethods(methods);
        setFlagCategories(flags);
      } catch (error) {
        console.error('Failed to load labour parameters:', error);
      } finally {
        setLoadingLabourParams(false);
      }
    };
    loadLabourParams();
  }, [token, isAdmin]);

  // Update selected org when user changes
  useEffect(() => {
    if (user?.current_organization_id) {
      setSelectedOrgId(user.current_organization_id);
    }
  }, [user?.current_organization_id]);

  const handleSwitchOrganization = async () => {
    if (!selectedOrgId || selectedOrgId === user?.current_organization_id) {
      return;
    }

    setSwitchingOrg(true);
    try {
      await switchOrganization(selectedOrgId);
      showToast('success', 'Organization switched successfully!');
      // Reload page to apply theme
      window.location.reload();
    } catch (error: any) {
      showToast('error', `Failed to switch organization: ${error.message}`);
    } finally {
      setSwitchingOrg(false);
    }
  };

  const handleCreateOrganization = async () => {
    if (!token || !newOrgData.name || !newOrgData.display_name) {
      showToast('error', 'Name and Display Name are required');
      return;
    }

    setIsLoading(true);
    try {
      await authApi.createOrganization(token, newOrgData);
      showToast('success', 'Organization created successfully!');
      setCreateOrgDialog(false);
      setNewOrgData({ name: '', display_name: '', description: '', theme: 'ppcanda', logo_url: '' });
      
      // Reload organizations
      const result = await authApi.getAllOrganizations(token);
      setOrganizations(result.organizations);
    } catch (error: any) {
      showToast('error', `Failed to create organization: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateOrganization = async () => {
    if (!token || !editingOrg) return;

    setIsLoading(true);
    try {
      await authApi.updateOrganization(token, editingOrg.id, {
        display_name: editingOrg.display_name,
        description: editingOrg.description,
        theme: editingOrg.theme,
        logo_url: editingOrg.logo_url
      });
      showToast('success', 'Organization updated successfully!');
      setEditOrgDialog(false);
      setEditingOrg(null);
      
      // Reload organizations
      const result = await authApi.getAllOrganizations(token);
      setOrganizations(result.organizations);
    } catch (error: any) {
      showToast('error', `Failed to update organization: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showToast('error', 'Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showToast('error', 'Image size must be less than 5MB');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result as string);
      };
      reader.readAsDataURL(file);

      // TODO: Upload to server and get URL
      // For now, we'll just show the preview
      showToast('info', 'Image selected. Upload functionality coming soon.');
    }
  };

  const handleUpdateProfile = async () => {
    if (!token) return;

    try {
      setIsLoading(true);
      await authApi.updateProfile(token, {
        full_name: fullName,
        profile_image_url: profileImage || undefined,
      });
      await refreshUser();
      showToast('success', 'Profile updated successfully!');
    } catch (error: any) {
      showToast('error', `Failed to update profile: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!token) return;

    // Validation
    if (!oldPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    try {
      setIsLoading(true);
      setPasswordError('');
      await authApi.changePassword(token, oldPassword, newPassword);
      showToast('success', 'Password changed successfully!');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      setPasswordError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveOpenRouter = async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const payload: { api_key?: string; llm_enabled: boolean } = {
        llm_enabled: llmEnabled,
      };
      if (openRouterKey.trim()) {
        payload.api_key = openRouterKey.trim();
      }
      const result = await authApi.updateOpenRouterSettings(token, payload);
      setOpenRouterConfigured(result.configured);
      setOpenRouterPreview(result.key_preview);
      setOpenRouterCredits(result.credits || null);
      setLlmEnabled(result.llm_enabled);
      setOpenRouterKey('');
      showToast('success', 'OpenRouter settings saved');
    } catch (error: any) {
      showToast('error', error.response?.data?.detail || 'Failed to save OpenRouter settings');
    } finally {
      setIsLoading(false);
    }
  };

  const erpSignature = JSON.stringify({
    mode: erpMode,
    endpoints: erpEndpoints.map((row) => ({
      key: row.key,
      demo_url: row.demo_url.trim(),
      live_url: row.live_url.trim(),
    })),
  });
  const erpDirty = Boolean(erpSavedSignature) && (erpSignature !== erpSavedSignature || Boolean(erpKey.trim()));

  const applyErpResult = (result: authApi.ErpSettings) => {
    const mode = result.mode === 'live' ? 'live' : 'demo';
    const endpoints = result.endpoints || [];
    setErpConfigured(result.configured);
    setErpPreview(result.key_preview);
    setErpMode(mode);
    setErpEndpoints(endpoints);
    setErpSavedSignature(JSON.stringify({
      mode,
      endpoints: endpoints.map((row) => ({
        key: row.key,
        demo_url: row.demo_url,
        live_url: row.live_url,
      })),
    }));
    setErpKey('');
  };

  const handleSaveErp = async () => {
    if (!token) return;
    if (erpMode === 'live') {
      setConfirmLiveErp(true);
      return;
    }
    await saveErpSettings();
  };

  const saveErpSettings = async () => {
    if (!token) return;
    setConfirmLiveErp(false);
    setIsLoading(true);
    try {
      const payload: {
        api_key?: string;
        mode: 'demo' | 'live';
        endpoints: { key: string; demo_url: string; live_url: string }[];
      } = {
        mode: erpMode,
        endpoints: erpEndpoints.map((row) => ({
          key: row.key,
          demo_url: row.demo_url.trim(),
          live_url: row.live_url.trim(),
        })),
      };
      if (erpKey.trim()) payload.api_key = erpKey.trim();
      const result = await authApi.updateErpSettings(token, payload);
      applyErpResult(result);
      showToast('success', result.mode === 'live' ? 'ERP set to Live' : 'ERP set to Demo');
    } catch (error: any) {
      showToast('error', error.response?.data?.detail || 'Failed to save ERP settings');
    } finally {
      setIsLoading(false);
    }
  };

  const updateErpUrl = (key: string, side: 'demo_url' | 'live_url', value: string) => {
    setErpEndpoints((current) => current.map((row) => {
      if (row.key !== key) return row;
      if (row.shared) return { ...row, demo_url: value, live_url: value };
      return { ...row, [side]: value };
    }));
  };

  const handleSaveLabourParam = async (param: LabourParam, raw: string) => {
    const value = Number(raw);
    if (Number.isNaN(value) || value === param.value) return;
    setSavingLabourParam(true);
    try {
      const updated = await quoteApi.updateLabourParam(param.id, value);
      setLabourParams((current) => current.map((row) => (row.id === updated.id ? updated : row)));
      if (updated.category === 'mounting') {
        setMountingMethods((current) => current.map((row) => (
          row.id === updated.id ? { ...row, seconds: updated.value } : row
        )));
      }
      showToast('success', `${updated.description || updated.key} saved`);
    } catch (error: any) {
      showToast('error', error.response?.data?.detail || 'Failed to save labour parameter');
    } finally {
      setSavingLabourParam(false);
    }
  };

  if (!user) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          Please log in to access settings.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Manage your account, system, and organisation settings
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, next) => setTab(next)}
        sx={{ mb: 2, minHeight: 48, '& .MuiTab-root': { textTransform: 'none', minHeight: 48, fontSize: '0.875rem' } }}
      >
        <Tab value="user" label="User settings" />
        <Tab value="system" label="System settings" />
        <Tab value="org" label="Org settings" />
      </Tabs>

      <Grid container spacing={3} sx={{ mt: 0 }}>
        {tab === 'user' && (
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Profile Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {/* Profile Image */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                src={profileImage || undefined}
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'primary.main',
                  fontSize: '2rem',
                  mr: 2,
                }}
              >
                {user.username[0].toUpperCase()}
              </Avatar>
              <Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="profile-image-upload"
                  type="file"
                  onChange={handleProfileImageChange}
                />
                <label htmlFor="profile-image-upload">
                  <IconButton
                    color="primary"
                    component="span"
                    sx={{ border: '1px dashed', borderColor: 'divider' }}
                  >
                    <PhotoCameraIcon />
                  </IconButton>
                </label>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Upload profile image (max 5MB)
                </Typography>
              </Box>
            </Box>

            {/* Full Name */}
            <TextField
              fullWidth
              label="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              sx={{ mb: 2 }}
            />

            {/* Email (read-only) */}
            <TextField
              fullWidth
              label="Email"
              value={user.email}
              disabled
              sx={{ mb: 2 }}
              helperText="Email cannot be changed"
            />

            {/* Username (read-only) */}
            <TextField
              fullWidth
              label="Username"
              value={user.username}
              disabled
              sx={{ mb: 3 }}
              helperText="Username cannot be changed"
            />

            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleUpdateProfile}
              disabled={isLoading}
            >
              Update Profile
            </Button>
          </Paper>
        </Grid>
        )}

        {tab === 'user' && (
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Change Password
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {passwordError}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Current Password"
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setPasswordError('');
              }}
              sx={{ mb: 2 }}
              helperText="Must be at least 8 characters"
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setPasswordError('');
              }}
              sx={{ mb: 3 }}
            />

            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleChangePassword}
              disabled={isLoading}
            >
              Change Password
            </Button>
          </Paper>
        </Grid>
        )}

        {tab === 'system' && isAdmin && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <VpnKeyIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
                  OpenRouter API
                </Typography>
                {openRouterConfigured && (
                  <IconButton
                    size="small"
                    onClick={async () => {
                      if (!token) return;
                      setLoadingOpenRouter(true);
                      try {
                        const result = await authApi.getOpenRouterSettings(token);
                        setOpenRouterConfigured(result.configured);
                        setOpenRouterPreview(result.key_preview);
                        setOpenRouterCredits(result.credits || null);
                        setLlmEnabled(result.llm_enabled);
                      } catch (error) {
                        console.error('Failed to refresh OpenRouter settings:', error);
                      } finally {
                        setLoadingOpenRouter(false);
                      }
                    }}
                    disabled={loadingOpenRouter}
                    aria-label="Refresh OpenRouter usage"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Divider sx={{ mb: 3 }} />

              {loadingOpenRouter ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Alert severity={openRouterConfigured ? 'success' : 'info'} sx={{ mb: 3 }}>
                    {openRouterConfigured
                      ? `A key is configured${openRouterPreview ? ` (${openRouterPreview})` : ''}. Enter a new key below to replace it.`
                      : 'No OpenRouter key is set. Add the key PPCanda provides to enable LLM enrichment.'}
                  </Alert>

                  {openRouterConfigured && openRouterCredits?.available && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={6} sm={3}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Account balance
                          </Typography>
                          <Typography variant="h6" fontWeight={600}>
                            {formatUsd(openRouterCredits.remaining)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Account used
                          </Typography>
                          <Typography variant="h6" fontWeight={600}>
                            {formatUsd(openRouterCredits.total_usage)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Key remaining{openRouterCredits.key_limit_reset ? ` (${openRouterCredits.key_limit_reset})` : ''}
                          </Typography>
                          <Typography variant="h6" fontWeight={600}>
                            {formatUsd(openRouterCredits.key_limit_remaining)}
                          </Typography>
                          {openRouterCredits.key_limit != null && (
                            <Typography variant="caption" color="text.secondary">
                              of {formatUsd(openRouterCredits.key_limit)}
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Used today
                          </Typography>
                          <Typography variant="h6" fontWeight={600}>
                            {formatUsd(openRouterCredits.usage_daily)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Key lifetime {formatUsd(openRouterCredits.key_usage)}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  )}

                  {openRouterConfigured && openRouterCredits && !openRouterCredits.available && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                      {openRouterCredits.error || 'Could not load OpenRouter balance and usage.'}
                    </Alert>
                  )}

                  <TextField
                    fullWidth
                    label="OpenRouter API Key"
                    type="password"
                    value={openRouterKey}
                    onChange={(e) => setOpenRouterKey(e.target.value)}
                    sx={{ mb: 2 }}
                    autoComplete="off"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <VpnKeyIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Stored on the server. The full key is never shown again after saving."
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={llmEnabled}
                        onChange={(e) => setLlmEnabled(e.target.checked)}
                      />
                    }
                    label="Enable LLM enrichment"
                    sx={{ mb: 3, display: 'block' }}
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveOpenRouter}
                    disabled={isLoading}
                  >
                    Save OpenRouter Settings
                  </Button>
                </>
              )}
            </Paper>
          </Grid>
        )}

        {tab === 'system' && !isAdmin && (
          <Grid item xs={12}>
            <Alert severity="info">Only administrators can change system settings (OpenRouter, ERP).</Alert>
          </Grid>
        )}

        {tab === 'system' && isAdmin && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <VpnKeyIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  ERP API
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              {loadingErp ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Alert severity={erpConfigured ? 'success' : 'info'} sx={{ mb: 2 }}>
                    {erpConfigured
                      ? `A subscription key is configured${erpPreview ? ` (${erpPreview})` : ''}.`
                      : 'No ERP subscription key is set. Required for cost centres, create, and update.'}
                  </Alert>
                  <Alert severity={erpMode === 'live' ? 'warning' : 'info'} sx={{ mb: 2 }}>
                    {erpMode === 'live'
                      ? 'Live is selected. After you save, create, update, and amend call the production ERP.'
                      : 'Demo is selected. Create, update, and amend stay on the -dev endpoints. Sync from ERP uses the live export in both modes.'}
                  </Alert>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="body2" fontWeight={600}>Target</Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={erpMode}
                      onChange={(_event, value: 'demo' | 'live' | null) => {
                        if (value) setErpMode(value);
                      }}
                    >
                      <ToggleButton value="demo">Demo</ToggleButton>
                      <ToggleButton value="live">Live</ToggleButton>
                    </ToggleButtonGroup>
                    <Chip
                      size="small"
                      label={erpMode === 'live' ? 'Will use Live URLs' : 'Will use Demo URLs'}
                      color={erpMode === 'live' ? 'warning' : 'default'}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                    {erpEndpoints.map((row) => (
                      <Box key={row.key}>
                        <Typography variant="body2" fontWeight={600}>{row.label}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          {row.method}{row.note ? ` · ${row.note}` : ''}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Demo"
                            value={row.demo_url}
                            onChange={(event) => updateErpUrl(row.key, 'demo_url', event.target.value)}
                            inputProps={{ spellCheck: false }}
                            sx={{
                              ...(erpMode === 'demo' ? { '& .MuiOutlinedInput-root': { bgcolor: 'rgba(249, 178, 51, 0.12)' } } : {}),
                              '& input': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 },
                            }}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            label="Live"
                            value={row.live_url}
                            onChange={(event) => updateErpUrl(row.key, 'live_url', event.target.value)}
                            inputProps={{ spellCheck: false }}
                            sx={{
                              ...(erpMode === 'live' ? { '& .MuiOutlinedInput-root': { bgcolor: 'rgba(249, 178, 51, 0.12)' } } : {}),
                              '& input': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 },
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <TextField
                    fullWidth
                    label="ERP subscription key"
                    type="password"
                    value={erpKey}
                    onChange={(e) => setErpKey(e.target.value)}
                    sx={{ mb: 2 }}
                    autoComplete="off"
                    helperText="Leave blank to keep the saved key. Stored as ERP_API_KEY."
                  />
                  <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveErp} disabled={isLoading || !erpDirty}>
                    Save ERP Settings
                  </Button>
                  <Dialog open={confirmLiveErp} onClose={() => setConfirmLiveErp(false)}>
                    <DialogTitle>Use the live ERP?</DialogTitle>
                    <DialogContent>
                      <Typography variant="body2" sx={{ pt: 1 }}>
                        Create, update, and amend will call the production endpoints. Demo stays available here if you switch back.
                      </Typography>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setConfirmLiveErp(false)}>Cancel</Button>
                      <Button variant="contained" color="warning" onClick={saveErpSettings} disabled={isLoading}>
                        Save Live
                      </Button>
                    </DialogActions>
                  </Dialog>
                </>
              )}
            </Paper>
          </Grid>
        )}

        {tab === 'org' && isAdmin && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimerIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  Labour defaults
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Alert severity="info" sx={{ mb: 2 }}>
                Rates for the current organisation ({user?.current_organization?.display_name || 'none selected'}).
                PPCANDA-TEST has its own copy — switch organisation before customer UAT so edits stay on TEST.
                Changing a value applies when labour is recalculated.
              </Alert>
              {loadingLabourParams ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                ([
                  { category: 'allowance', title: 'Allowances', hint: 'Compounded onto base hours (Excel: Test 10%, Material 10%, H&B 14%, Batch 80%).' },
                  { category: 'wiring', title: 'Wiring times', hint: 'Seconds per connection / cable — electrical hours.' },
                  { category: 'install', title: 'Install times', hint: 'Seconds per device by mounting method — mechanical hours.' },
                ] as const).map((group) => (
                  <Box key={group.category} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600}>{group.title}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {group.hint}
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Parameter</TableCell>
                          <TableCell align="right" sx={{ width: 140 }}>Value</TableCell>
                          <TableCell sx={{ width: 90 }}>Unit</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {labourParams.filter((param) => param.category === group.category).map((param) => (
                          <TableRow key={param.id}>
                            <TableCell>{param.description || param.key}</TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                defaultValue={param.value}
                                key={`${param.id}-${param.value}`}
                                disabled={savingLabourParam}
                                onBlur={(e) => handleSaveLabourParam(param, e.target.value)}
                                inputProps={{ step: param.unit === 'percent' ? 1 : 10, min: 0 }}
                                sx={{ width: 110 }}
                              />
                            </TableCell>
                            <TableCell>{param.unit}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                ))
              )}
            </Paper>
          </Grid>
        )}

        {tab === 'org' && isAdmin && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Fitting methods</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Standard install methods plus any extras estimators need (e.g. Cable/Wiring, Cable Accessories). Seconds are used on Labour / Review mechanical totals.
              </Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Method</TableCell>
                    <TableCell align="center" sx={{ width: 140 }}>No-connections</TableCell>
                    <TableCell align="right" sx={{ width: 140 }}>Seconds</TableCell>
                    <TableCell sx={{ width: 90 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mountingMethods.map((method) => (
                    <TableRow key={method.id || method.key}>
                      <TableCell>{method.label}</TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          disabled={savingLabourParam || method.id == null}
                          checked={Boolean(method.no_connections)}
                          onChange={async (e) => {
                            if (method.id == null) return;
                            setSavingLabourParam(true);
                            try {
                              const updated = await quoteApi.updateLabourParam(method.id, undefined, { no_connections: e.target.checked });
                              setMountingMethods((current) => current.map((row) => (
                                row.id === method.id ? { ...row, no_connections: Boolean((updated as any).no_connections ?? e.target.checked) } : row
                              )));
                            } catch (error: any) {
                              showToast('error', error.response?.data?.detail || 'Failed to save no-connections');
                            } finally {
                              setSavingLabourParam(false);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {method.id != null ? (
                          <TextField
                            size="small"
                            type="number"
                            defaultValue={method.seconds}
                            key={`${method.id}-${method.seconds}`}
                            disabled={savingLabourParam}
                            onBlur={(e) => {
                              if (method.id == null) return;
                              handleSaveLabourParam(
                                { id: method.id, category: 'mounting', key: method.key, value: method.seconds, unit: 'seconds', description: method.label },
                                e.target.value
                              );
                            }}
                            inputProps={{ min: 0, step: 10 }}
                            sx={{ width: 110 }}
                          />
                        ) : (
                          method.seconds
                        )}
                      </TableCell>
                      <TableCell>
                        {!method.seeded && method.id != null && (
                          <Button
                            size="small"
                            color="error"
                            disabled={savingMounting}
                            onClick={async () => {
                              setSavingMounting(true);
                              try {
                                await quoteApi.deleteMountingMethod(method.id as number);
                                setMountingMethods((current) => current.filter((row) => row.id !== method.id));
                              } finally {
                                setSavingMounting(false);
                              }
                            }}
                          >
                            Remove
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', maxWidth: 720, alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="New method"
                  value={newMountingName}
                  onChange={(e) => setNewMountingName(e.target.value)}
                  sx={{ flex: 1, minWidth: 180 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Seconds"
                  value={newMountingSeconds}
                  onChange={(e) => setNewMountingSeconds(e.target.value)}
                  sx={{ width: 120 }}
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={newMountingNoConnect} onChange={(e) => setNewMountingNoConnect(e.target.checked)} />}
                  label="No-connections"
                />
                <Button
                  variant="contained"
                  disabled={savingMounting || !newMountingName.trim()}
                  onClick={async () => {
                    setSavingMounting(true);
                    try {
                      const created = await quoteApi.addMountingMethod(newMountingName.trim(), Number(newMountingSeconds) || 0, newMountingNoConnect);
                      setMountingMethods((current) => [...current.filter((row) => row.key !== created.key), created]);
                      setNewMountingName('');
                      setNewMountingSeconds('200');
                      setNewMountingNoConnect(false);
                    } finally {
                      setSavingMounting(false);
                    }
                  }}
                >
                  Add method
                </Button>
              </Box>
            </Paper>
          </Grid>
        )}

        {tab === 'org' && isAdmin && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Labour flag categories</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Estimators can tag lines with these flags (e.g. Heavy, High density). Categories belong to the current organisation.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {flagCategories.map((flag) => (
                  <Chip
                    key={flag.id}
                    label={flag.label}
                    onDelete={async () => {
                      await quoteApi.deleteFlagCategory(flag.id);
                      setFlagCategories((current) => current.filter((item) => item.id !== flag.id));
                    }}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, maxWidth: 420 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="New flag"
                  value={newFlagLabel}
                  onChange={(e) => setNewFlagLabel(e.target.value)}
                />
                <Button
                  variant="contained"
                  disabled={!newFlagLabel.trim()}
                  onClick={async () => {
                    const created = await quoteApi.addFlagCategory(newFlagLabel.trim());
                    setFlagCategories((current) => [...current.filter((item) => item.id !== created.id), created]);
                    setNewFlagLabel('');
                  }}
                >
                  Add
                </Button>
              </Box>
            </Paper>
          </Grid>
        )}

        {tab === 'org' && user?.is_superadmin && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  Organization Management
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              {loadingOrgs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : organizations.length > 0 ? (
                <>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Switch Organization</InputLabel>
                    <Select
                      value={selectedOrgId}
                      onChange={(e) => setSelectedOrgId(e.target.value)}
                      label="Switch Organization"
                      disabled={switchingOrg}
                    >
                      {organizations.map((org) => (
                        <MenuItem key={org.id} value={org.id}>
                          {org.display_name} {org.id === user?.current_organization_id ? '(Current)' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedOrgId && selectedOrgId !== user?.current_organization_id && (
                    <Button
                      variant="contained"
                      onClick={handleSwitchOrganization}
                      disabled={switchingOrg || !selectedOrgId}
                      sx={{ mb: 2 }}
                    >
                      {switchingOrg ? 'Switching...' : 'Switch Organization'}
                    </Button>
                  )}

                  <Alert severity={user?.current_organization ? "info" : "warning"} sx={{ mt: 2, mb: 3 }}>
                    <Typography variant="body2">
                      <strong>Current Organization:</strong>{' '}
                      {user?.current_organization?.display_name || 'None (Please select an organization)'}
                    </Typography>
                    {!user?.current_organization && (
                      <Typography variant="body2" sx={{ mt: 1, color: 'warning.dark' }}>
                        ⚠️ No organization selected. Please select an organization above.
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Switching organizations will reload the page to apply the organization's theme and settings.
                    </Typography>
                  </Alert>

                  <Divider sx={{ my: 3 }} />

                  {/* Organization Management */}
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Manage Organizations
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setCreateOrgDialog(true)}
                      size="small"
                    >
                      Add Organization
                    </Button>
                  </Box>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Theme</TableCell>
                        <TableCell>Users</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {organizations.map((org) => (
                        <TableRow key={org.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {org.logo_url && !/getvia\.ai|via\.ai/i.test(org.logo_url) && (
                                <Box
                                  component="img"
                                  src={org.logo_url}
                                  alt={org.display_name}
                                  sx={{ height: 20, maxWidth: 40 }}
                                />
                              )}
                              <Typography variant="body2">{org.display_name}</Typography>
                              {org.id === user?.current_organization_id && (
                                <Chip label="Current" size="small" color="primary" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={org.theme === 'indigo' ? 'Indigo' : 'PPCANDA'}
                              size="small"
                              color={org.theme === 'indigo' ? 'secondary' : 'primary'}
                            />
                          </TableCell>
                          <TableCell>{org.user_count || 0}</TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingOrg(org);
                                setEditOrgDialog(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              ) : (
                <Alert severity="info">
                  No organizations available. Contact system administrator.
                </Alert>
              )}
            </Paper>
          </Grid>
        )}

        {tab === 'org' && !isAdmin && !user?.is_superadmin && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>Organisation</Typography>
              <Typography variant="body2" color="text.secondary">
                Current organisation: {user?.current_organization?.display_name || 'None selected'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Labour defaults are admin-only.
              </Typography>
            </Paper>
          </Grid>
        )}

        {tab === 'user' && (
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Account Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  User ID
                </Typography>
                <Typography variant="body1">{user.id}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Account Type
                </Typography>
                <Typography variant="body1">
                  {user.is_superadmin
                    ? 'Super Admin'
                    : user.is_admin
                    ? 'Admin'
                    : 'User'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Roles
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {user.roles.map((role) => (
                    <Typography
                      key={role.id}
                      variant="caption"
                      sx={{
                        px: 1,
                        py: 0.25,
                        bgcolor: 'primary.light',
                        color: 'primary.contrastText',
                        borderRadius: 0.5,
                      }}
                    >
                      {role.display_name}
                    </Typography>
                  ))}
                </Box>
              </Grid>
              {(user as any).last_login && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Last Login
                  </Typography>
                  <Typography variant="body1">
                    {new Date((user as any).last_login).toLocaleString()}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
        )}
      </Grid>

      {/* Create Organization Dialog */}
      <Dialog open={createOrgDialog} onClose={() => setCreateOrgDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Organization</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Organization Name (ID)"
            value={newOrgData.name}
            onChange={(e) => setNewOrgData({ ...newOrgData, name: e.target.value.toLowerCase().replace(/\s+/g, '') })}
            sx={{ mb: 2, mt: 1 }}
            helperText="Lowercase, no spaces (e.g., 'mycompany')"
          />
          <TextField
            fullWidth
            label="Display Name"
            value={newOrgData.display_name}
            onChange={(e) => setNewOrgData({ ...newOrgData, display_name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={newOrgData.description}
            onChange={(e) => setNewOrgData({ ...newOrgData, description: e.target.value })}
            sx={{ mb: 2 }}
            multiline
            rows={2}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Theme</InputLabel>
            <Select
              value={newOrgData.theme}
              onChange={(e) => setNewOrgData({ ...newOrgData, theme: e.target.value as 'ppcanda' | 'indigo' })}
              label="Theme"
            >
              <MenuItem value="ppcanda">PPCANDA (Blue/Yellow)</MenuItem>
              <MenuItem value="indigo">Indigo (Purple)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Logo URL"
            value={newOrgData.logo_url}
            onChange={(e) => setNewOrgData({ ...newOrgData, logo_url: e.target.value })}
            sx={{ mb: 2 }}
            helperText="URL to organization logo (e.g., https://example.com/logo.png or /logo.svg)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOrgDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateOrganization}
            disabled={!newOrgData.name || !newOrgData.display_name || isLoading}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Organization Dialog */}
      <Dialog open={editOrgDialog} onClose={() => setEditOrgDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Organization</DialogTitle>
        <DialogContent>
          {editingOrg && (
            <>
              <TextField
                fullWidth
                label="Organization Name"
                value={editingOrg.name}
                disabled
                sx={{ mb: 2, mt: 1 }}
                helperText="Organization name cannot be changed"
              />
              <TextField
                fullWidth
                label="Display Name"
                value={editingOrg.display_name}
                onChange={(e) => setEditingOrg({ ...editingOrg, display_name: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Description"
                value={editingOrg.description || ''}
                onChange={(e) => setEditingOrg({ ...editingOrg, description: e.target.value })}
                sx={{ mb: 2 }}
                multiline
                rows={2}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Theme</InputLabel>
                <Select
                  value={editingOrg.theme}
                  onChange={(e) => setEditingOrg({ ...editingOrg, theme: e.target.value })}
                  label="Theme"
                >
                  <MenuItem value="ppcanda">PPCANDA (Blue/Yellow)</MenuItem>
                  <MenuItem value="indigo">Indigo (Purple)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Logo URL"
                value={editingOrg.logo_url || ''}
                onChange={(e) => setEditingOrg({ ...editingOrg, logo_url: e.target.value })}
                sx={{ mb: 2 }}
                helperText="URL to organization logo"
              />
              {editingOrg.logo_url && !/getvia\.ai|via\.ai/i.test(editingOrg.logo_url) && (
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                  <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                    Logo Preview:
                  </Typography>
                  <Box
                    component="img"
                    src={editingOrg.logo_url}
                    alt="Logo preview"
                    sx={{ maxHeight: 50, maxWidth: 200 }}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOrgDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateOrganization}
            disabled={isLoading}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;

